import { z } from 'zod';
import {
  baseRow,
  cents,
  customFieldValues,
  dateOnly,
  email,
  id,
  isoDate,
  listInput,
  nonEmpty,
  nonNegativeCents,
  optionalText,
  quantity,
  signatureSchema,
  expectedVersion,
  updateOf,
} from './common';
import { costCategory } from './crm';

/**
 * Like `updateOf`, but fields keep no `.default()`: with zod 4, `.partial()` still applies
 * defaults, so a partial update would silently reset omitted fields to their defaults.
 */
function patchOf<T extends z.ZodRawShape>(create: z.ZodObject<T>): ReturnType<typeof updateOf<T>> {
  const shape: Record<string, z.ZodType> = {};
  for (const [key, field] of Object.entries(create.shape)) {
    const inner = field instanceof z.ZodDefault ? (field.unwrap() as z.ZodType) : (field as z.ZodType);
    shape[key] = inner.optional();
  }
  return z.object(shape).extend({ id, expectedVersion }) as unknown as ReturnType<typeof updateOf<T>>;
}

// ---------------------------------------------------------------------------
// Enumerations
// ---------------------------------------------------------------------------

export const CHANGE_ORDER_STATUSES = ['draft', 'pending_internal', 'sent', 'approved', 'rejected', 'void'] as const;
export const changeOrderStatus = z.enum(CHANGE_ORDER_STATUSES);
export type ChangeOrderStatus = z.infer<typeof changeOrderStatus>;

export const INVOICE_STATUSES = ['draft', 'sent', 'viewed', 'partially_paid', 'paid', 'overdue', 'void'] as const;
export const invoiceStatus = z.enum(INVOICE_STATUSES);
export type InvoiceStatus = z.infer<typeof invoiceStatus>;

export const INVOICE_KINDS = ['standard', 'progress', 'deposit', 'retainage', 'change_order', 'final'] as const;
export const invoiceKind = z.enum(INVOICE_KINDS);
export type InvoiceKind = z.infer<typeof invoiceKind>;

export const PAYMENT_METHODS = ['card', 'ach', 'check', 'cash', 'wire', 'other'] as const;
export const paymentMethod = z.enum(PAYMENT_METHODS);
export type PaymentMethod = z.infer<typeof paymentMethod>;

export const PAYMENT_STATUSES = ['pending', 'processing', 'succeeded', 'failed', 'refunded', 'partially_refunded', 'cancelled'] as const;
export const paymentStatus = z.enum(PAYMENT_STATUSES);
export type PaymentStatus = z.infer<typeof paymentStatus>;

export const LIEN_WAIVER_TYPES = ['conditional_progress', 'unconditional_progress', 'conditional_final', 'unconditional_final'] as const;
export const lienWaiverType = z.enum(LIEN_WAIVER_TYPES);
export type LienWaiverType = z.infer<typeof lienWaiverType>;

export const LIEN_WAIVER_STATUSES = ['draft', 'requested', 'received', 'signed', 'rejected', 'void'] as const;
export const lienWaiverStatus = z.enum(LIEN_WAIVER_STATUSES);
export type LienWaiverStatus = z.infer<typeof lienWaiverStatus>;

export const LIEN_WAIVER_DIRECTIONS = ['outgoing', 'incoming'] as const;
export const lienWaiverDirection = z.enum(LIEN_WAIVER_DIRECTIONS);

// ---------------------------------------------------------------------------
// Budget
// ---------------------------------------------------------------------------

export const createBudgetLineInput = z.object({
  projectId: id,
  costCode: z.string().trim().max(40).optional().nullable(),
  category: costCategory.default('material'),
  description: nonEmpty(500),
  originalCents: cents.default(0),
  estimateLineId: id.optional().nullable(),
  position: z.number().int().default(0),
});
export type CreateBudgetLineInput = z.infer<typeof createBudgetLineInput>;
export const updateBudgetLineInput = patchOf(createBudgetLineInput.omit({ projectId: true })).extend({
  committedCents: cents.optional(),
  actualCents: cents.optional(),
});
export const budgetLineSchema = baseRow.extend({
  projectId: id,
  costCode: z.string().nullable(),
  category: costCategory,
  description: z.string(),
  originalCents: cents,
  approvedChangesCents: cents,
  committedCents: cents,
  actualCents: cents,
  estimateLineId: id.nullable(),
  position: z.number().int(),
});
export type BudgetLine = z.infer<typeof budgetLineSchema>;

// ---------------------------------------------------------------------------
// Change orders
// ---------------------------------------------------------------------------

export const changeOrderLineInput = z.object({
  id: id.optional(),
  position: z.number().int().min(0),
  costCode: z.string().trim().max(40).optional().nullable(),
  category: costCategory.default('material'),
  description: nonEmpty(1000),
  quantity: quantity.default(1),
  unit: z.string().trim().max(20).default('ea'),
  unitCostCents: cents.default(0),
  markupBps: z.number().int().min(-10_000).max(100_000).optional().nullable(),
  taxable: z.boolean().default(true),
  budgetLineId: id.optional().nullable(),
});
export type ChangeOrderLineInput = z.infer<typeof changeOrderLineInput>;
export const changeOrderLineSchema = baseRow.extend({
  changeOrderId: id,
  position: z.number().int(),
  costCode: z.string().nullable(),
  category: costCategory,
  description: z.string(),
  quantity: z.number(),
  unit: z.string(),
  unitCostCents: cents,
  markupBps: z.number().int().nullable(),
  costTotalCents: cents,
  priceTotalCents: cents,
  taxable: z.boolean(),
  budgetLineId: id.nullable(),
});
export type ChangeOrderLine = z.infer<typeof changeOrderLineSchema>;

export const createChangeOrderInput = z.object({
  projectId: id,
  title: nonEmpty(200),
  description: optionalText(50_000),
  reason: z.enum(['client_request', 'unforeseen_condition', 'design_change', 'allowance_adjustment', 'error_omission', 'other']).default('client_request'),
  scheduleImpactDays: z.number().int().min(-3650).max(3650).default(0),
  taxRateBps: z.number().int().min(0).max(10_000).optional(),
  rfiId: id.optional().nullable(),
  visibleToClient: z.boolean().default(true),
  lines: z.array(changeOrderLineInput).max(1000).default([]),
  custom: customFieldValues.default({}),
});
export type CreateChangeOrderInput = z.infer<typeof createChangeOrderInput>;
export const updateChangeOrderInput = patchOf(createChangeOrderInput.omit({ projectId: true }));
export type UpdateChangeOrderInput = z.infer<typeof updateChangeOrderInput>;
export const changeOrderSchema = baseRow.extend({
  projectId: id,
  number: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  reason: z.string(),
  status: changeOrderStatus,
  scheduleImpactDays: z.number().int(),
  taxRateBps: z.number().int(),
  subtotalCents: cents,
  taxCents: cents,
  totalCents: cents,
  rfiId: id.nullable(),
  visibleToClient: z.boolean(),
  sentAt: z.date().nullable(),
  approvedAt: z.date().nullable(),
  rejectedAt: z.date().nullable(),
  rejectionReason: z.string().nullable(),
  signature: signatureSchema.nullable(),
  approvedByContactId: id.nullable(),
  custom: customFieldValues,
});
export type ChangeOrder = z.infer<typeof changeOrderSchema>;
export const changeOrderWithLinesSchema = changeOrderSchema.extend({ lines: z.array(changeOrderLineSchema) });
export type ChangeOrderWithLines = z.infer<typeof changeOrderWithLinesSchema>;
export const listChangeOrdersInput = listInput.extend({ projectId: id.optional(), status: changeOrderStatus.optional() });
export const approveChangeOrderInput = z.object({ id, signature: signatureSchema });
export const rejectChangeOrderInput = z.object({ id, reason: optionalText(5000) });

// ---------------------------------------------------------------------------
// Invoices + payments
// ---------------------------------------------------------------------------

export const invoiceLineInput = z.object({
  id: id.optional(),
  position: z.number().int().min(0),
  description: nonEmpty(1000),
  quantity: quantity.default(1),
  unit: z.string().trim().max(20).default('ea'),
  unitPriceCents: cents.default(0),
  taxable: z.boolean().default(true),
  costCode: z.string().trim().max(40).optional().nullable(),
  budgetLineId: id.optional().nullable(),
  changeOrderId: id.optional().nullable(),
  /** Progress billing: percent of scheduled value billed this period (basis points). */
  percentCompleteBps: z.number().int().min(0).max(10_000).optional().nullable(),
  scheduledValueCents: cents.optional().nullable(),
});
export type InvoiceLineInput = z.infer<typeof invoiceLineInput>;
export const invoiceLineSchema = baseRow.extend({
  invoiceId: id,
  position: z.number().int(),
  description: z.string(),
  quantity: z.number(),
  unit: z.string(),
  unitPriceCents: cents,
  totalCents: cents,
  taxable: z.boolean(),
  costCode: z.string().nullable(),
  budgetLineId: id.nullable(),
  changeOrderId: id.nullable(),
  percentCompleteBps: z.number().int().nullable(),
  scheduledValueCents: cents.nullable(),
});
export type InvoiceLine = z.infer<typeof invoiceLineSchema>;

export const createInvoiceInput = z.object({
  projectId: id.optional().nullable(),
  contactId: id,
  companyId: id.optional().nullable(),
  kind: invoiceKind.default('standard'),
  issueDate: dateOnly,
  dueDate: dateOnly,
  taxRateBps: z.number().int().min(0).max(10_000).optional(),
  retainageBps: z.number().int().min(0).max(10_000).optional(),
  memo: optionalText(10_000),
  allowCard: z.boolean().default(true),
  allowAch: z.boolean().default(true),
  lines: z.array(invoiceLineInput).min(1).max(1000),
  custom: customFieldValues.default({}),
});
export type CreateInvoiceInput = z.infer<typeof createInvoiceInput>;
export const updateInvoiceInput = patchOf(createInvoiceInput);
export type UpdateInvoiceInput = z.infer<typeof updateInvoiceInput>;
export const invoiceSchema = baseRow.extend({
  projectId: id.nullable(),
  contactId: id,
  companyId: id.nullable(),
  number: z.string(),
  kind: invoiceKind,
  status: invoiceStatus,
  issueDate: z.string(),
  dueDate: z.string(),
  taxRateBps: z.number().int(),
  retainageBps: z.number().int(),
  subtotalCents: cents,
  taxCents: cents,
  retainageCents: cents,
  totalCents: cents,
  amountPaidCents: cents,
  balanceCents: cents,
  memo: z.string().nullable(),
  allowCard: z.boolean(),
  allowAch: z.boolean(),
  sentAt: z.date().nullable(),
  viewedAt: z.date().nullable(),
  paidAt: z.date().nullable(),
  publicToken: z.string().nullable(),
  stripeCheckoutSessionId: z.string().nullable(),
  custom: customFieldValues,
});
export type Invoice = z.infer<typeof invoiceSchema>;
export const invoiceWithLinesSchema = invoiceSchema.extend({ lines: z.array(invoiceLineSchema) });
export type InvoiceWithLines = z.infer<typeof invoiceWithLinesSchema>;
export const listInvoicesInput = listInput.extend({
  projectId: id.optional(),
  contactId: id.optional(),
  status: invoiceStatus.optional(),
  overdueOnly: z.boolean().default(false),
});
export const sendInvoiceInput = z.object({
  id,
  to: z.array(email).min(1).max(10),
  subject: nonEmpty(300),
  message: optionalText(10_000),
});

export const recordPaymentInput = z.object({
  invoiceId: id,
  amountCents: nonNegativeCents.refine((v) => v > 0, 'Amount must be positive'),
  method: paymentMethod,
  receivedAt: isoDate,
  reference: z.string().trim().max(120).optional().nullable(),
  notes: optionalText(2000),
});
export type RecordPaymentInput = z.infer<typeof recordPaymentInput>;
export const paymentSchema = baseRow.extend({
  invoiceId: id,
  projectId: id.nullable(),
  contactId: id.nullable(),
  amountCents: cents,
  feeCents: cents,
  refundedCents: cents,
  method: paymentMethod,
  status: paymentStatus,
  receivedAt: z.date().nullable(),
  reference: z.string().nullable(),
  notes: z.string().nullable(),
  stripePaymentIntentId: z.string().nullable(),
  stripeChargeId: z.string().nullable(),
  failureReason: z.string().nullable(),
});
export type Payment = z.infer<typeof paymentSchema>;
export const listPaymentsInput = listInput.extend({ invoiceId: id.optional(), projectId: id.optional(), status: paymentStatus.optional() });
export const refundPaymentInput = z.object({ id, amountCents: nonNegativeCents.optional(), reason: optionalText(500) });

/** Portal checkout: client pays an invoice via Stripe Checkout (card or ACH). */
export const createCheckoutInput = z.object({
  invoiceId: id,
  method: z.enum(['card', 'ach']),
  amountCents: nonNegativeCents.optional(),
  returnUrl: z.url({ protocol: /^https?$/ }).max(2048),
});
export type CreateCheckoutInput = z.infer<typeof createCheckoutInput>;

// ---------------------------------------------------------------------------
// Lien waivers
// ---------------------------------------------------------------------------

export const createLienWaiverInput = z.object({
  projectId: id,
  type: lienWaiverType,
  direction: lienWaiverDirection.default('incoming'),
  companyId: id.optional().nullable(),
  contactId: id.optional().nullable(),
  paymentId: id.optional().nullable(),
  invoiceId: id.optional().nullable(),
  /** Sub pay application the waiver covers (incoming waivers). */
  subPayAppId: id.optional().nullable(),
  amountCents: nonNegativeCents,
  throughDate: dateOnly,
  exceptions: optionalText(5000),
  jurisdiction: z.string().trim().max(8).optional().nullable(),
});
export type CreateLienWaiverInput = z.infer<typeof createLienWaiverInput>;
export const updateLienWaiverInput = patchOf(createLienWaiverInput.omit({ projectId: true })).extend({
  status: lienWaiverStatus.optional(),
});
export const lienWaiverSchema = baseRow.extend({
  projectId: id,
  type: lienWaiverType,
  direction: lienWaiverDirection,
  status: lienWaiverStatus,
  companyId: id.nullable(),
  contactId: id.nullable(),
  paymentId: id.nullable(),
  invoiceId: id.nullable(),
  amountCents: cents,
  throughDate: z.string(),
  exceptions: z.string().nullable(),
  jurisdiction: z.string().nullable(),
  documentId: id.nullable(),
  requestedAt: z.date().nullable(),
  signedAt: z.date().nullable(),
  signature: signatureSchema.nullable(),
  publicToken: z.string().nullable(),
  /** Sub pay application this waiver covers (incoming waivers from subcontractors). */
  subPayAppId: id.nullable(),
});
export type LienWaiver = z.infer<typeof lienWaiverSchema>;
export const listLienWaiversInput = listInput.extend({
  projectId: id.optional(),
  companyId: id.optional(),
  status: lienWaiverStatus.optional(),
});
export const signLienWaiverInput = z.object({ token: z.string().min(16).max(256), signature: signatureSchema });

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------

export const projectFinancialSummary = z.object({
  projectId: id,
  contractValueCents: cents,
  approvedChangeOrdersCents: cents,
  revisedContractCents: cents,
  budgetCents: cents,
  committedCents: cents,
  actualCostCents: cents,
  invoicedCents: cents,
  paidCents: cents,
  outstandingCents: cents,
  retainageHeldCents: cents,
  projectedMarginCents: cents,
});
export type ProjectFinancialSummary = z.infer<typeof projectFinancialSummary>;
export const arAgingReport = z.array(
  z.object({
    contactId: id,
    name: z.string(),
    currentCents: cents,
    days30Cents: cents,
    days60Cents: cents,
    days90Cents: cents,
    over90Cents: cents,
  }),
);

// ===========================================================================
// Financials module extensions (cost codes, budget queries, commitments, change events,
// commitment change orders, pay applications, sub pay apps, accounting sync, reports).
// ===========================================================================

export const CHANGE_REASONS = ['client_request', 'unforeseen_condition', 'design_change', 'allowance_adjustment', 'error_omission', 'other'] as const;
export const changeReason = z.enum(CHANGE_REASONS);
export type ChangeReason = z.infer<typeof changeReason>;

// ---------------------------------------------------------------------------
// Cost codes
// ---------------------------------------------------------------------------

/** Where a library entry comes from: the workspace's own codes, the CSI seed, project templates or budgets. */
export const COST_CODE_SOURCES = ['custom', 'csi', 'template', 'in_use'] as const;
export const costCodeSource = z.enum(COST_CODE_SOURCES);
export type CostCodeSource = z.infer<typeof costCodeSource>;
export const costCodeEntrySchema = z.object({
  code: z.string(),
  name: z.string(),
  division: z.string(),
  divisionName: z.string(),
  level: z.enum(['division', 'section']),
  source: costCodeSource,
  /** `cost_code` row id for workspace codes (source `custom`), null otherwise. */
  id: id.nullable(),
  category: costCategory.nullable(),
  description: z.string().nullable(),
});
export type CostCodeEntry = z.infer<typeof costCodeEntrySchema>;
export const listCostCodesInput = z.object({
  search: z.string().trim().max(100).optional(),
  /** Preview another business type's seed (defaults to the workspace's). */
  businessType: z.enum(['residential', 'remodeler', 'commercial', 'specialty_sub']).optional(),
  includeDivisions: z.boolean().default(true),
});

// ---------------------------------------------------------------------------
// Budget queries
// ---------------------------------------------------------------------------

export const EAC_METHODS = ['auto', 'remaining_budget', 'committed', 'performance', 'manual'] as const;
export const eacMethod = z.enum(EAC_METHODS);
export type EacMethod = z.infer<typeof eacMethod>;

export const projectBudgetInput = z.object({ projectId: id, method: eacMethod.default('auto'), includeArchived: z.boolean().default(false) });
export const seedBudgetInput = z.object({
  projectId: id,
  lines: z
    .array(
      z.object({
        costCode: z.string().trim().min(1).max(40),
        description: nonEmpty(500),
        category: costCategory.default('material'),
        originalCents: cents.default(0),
      }),
    )
    .min(1)
    .max(500),
});
export const importBudgetFromEstimateInput = z.object({ projectId: id, estimateId: id, replaceExisting: z.boolean().default(false) });

// ---------------------------------------------------------------------------
// Commitments (subcontracts + purchase orders)
// ---------------------------------------------------------------------------

export const COMMITMENT_KINDS = ['subcontract', 'purchase_order'] as const;
export const commitmentKind = z.enum(COMMITMENT_KINDS);
export type CommitmentKind = z.infer<typeof commitmentKind>;
export const COMMITMENT_STATUSES = ['draft', 'sent', 'executed', 'closed', 'void'] as const;
export const commitmentStatus = z.enum(COMMITMENT_STATUSES);
export type CommitmentStatus = z.infer<typeof commitmentStatus>;

export const commitmentLineInput = z.object({
  id: id.optional(),
  position: z.number().int().min(0),
  costCode: z.string().trim().max(40).optional().nullable(),
  category: costCategory.default('subcontract'),
  description: nonEmpty(1000),
  quantity: quantity.default(1),
  unit: z.string().trim().max(20).default('ls'),
  unitCostCents: cents.default(0),
  budgetLineId: id.optional().nullable(),
});
export type CommitmentLineInput = z.infer<typeof commitmentLineInput>;

export const createCommitmentInput = z.object({
  projectId: id,
  kind: commitmentKind.default('subcontract'),
  title: nonEmpty(200),
  companyId: id,
  contactId: id.optional().nullable(),
  description: optionalText(50_000),
  retainageBps: z.number().int().min(0).max(10_000).optional(),
  startDate: dateOnly.optional().nullable(),
  endDate: dateOnly.optional().nullable(),
  lines: z.array(commitmentLineInput).max(500).default([]),
});
export type CreateCommitmentInput = z.infer<typeof createCommitmentInput>;
export const updateCommitmentInput = patchOf(createCommitmentInput.omit({ projectId: true }));
export const setCommitmentStatusInput = z.object({ id, status: commitmentStatus, expectedVersion: z.number().int().optional() });
export const listCommitmentsInput = listInput.extend({ projectId: id.optional(), status: commitmentStatus.optional(), companyId: id.optional() });

export const commitmentLineSchema = z.object({
  id,
  position: z.number().int(),
  costCode: z.string().nullable(),
  category: costCategory,
  description: z.string(),
  quantity: z.number(),
  unit: z.string(),
  unitCostCents: cents,
  totalCents: cents,
  budgetLineId: id.nullable(),
});
export type CommitmentLine = z.infer<typeof commitmentLineSchema>;

// ---------------------------------------------------------------------------
// Change events + commitment change orders
// ---------------------------------------------------------------------------

export const CHANGE_EVENT_STATUSES = ['open', 'pending', 'converted', 'void'] as const;
export const changeEventStatus = z.enum(CHANGE_EVENT_STATUSES);
export type ChangeEventStatus = z.infer<typeof changeEventStatus>;
export const CHANGE_EVENT_SCOPES = ['tbd', 'in_scope', 'out_of_scope', 'allowance'] as const;
export const changeEventScope = z.enum(CHANGE_EVENT_SCOPES);

export const changeEventLineInput = z.object({
  id: id.optional(),
  position: z.number().int().min(0),
  costCode: z.string().trim().max(40).optional().nullable(),
  category: costCategory.default('material'),
  description: nonEmpty(1000),
  quantity: quantity.default(1),
  unit: z.string().trim().max(20).default('ea'),
  unitCostCents: cents.default(0),
  markupBps: z.number().int().min(-10_000).max(100_000).optional().nullable(),
  budgetLineId: id.optional().nullable(),
  commitmentId: id.optional().nullable(),
});
export type ChangeEventLineInput = z.infer<typeof changeEventLineInput>;

export const createChangeEventInput = z.object({
  projectId: id,
  title: nonEmpty(200),
  description: optionalText(50_000),
  reason: changeReason.default('client_request'),
  scope: changeEventScope.default('tbd'),
  rfiId: id.optional().nullable(),
  lines: z.array(changeEventLineInput).max(500).default([]),
});
export const updateChangeEventInput = patchOf(createChangeEventInput.omit({ projectId: true })).extend({
  status: z.enum(['open', 'pending', 'void']).optional(),
});
export const listChangeEventsInput = listInput.extend({ projectId: id.optional(), status: changeEventStatus.optional() });
export const convertChangeEventInput = z.object({
  id,
  createPrime: z.boolean().default(true),
  primeTitle: z.string().trim().max(200).optional(),
  /** Commitments to create commitment change orders for (lines tagged with that commitment). */
  commitmentIds: z.array(id).max(50).default([]),
});

export const COMMITMENT_CO_STATUSES = ['draft', 'pending', 'approved', 'rejected', 'void'] as const;
export const commitmentChangeOrderStatus = z.enum(COMMITMENT_CO_STATUSES);
export type CommitmentChangeOrderStatus = z.infer<typeof commitmentChangeOrderStatus>;
export const createCommitmentChangeOrderInput = z.object({
  commitmentId: id,
  title: nonEmpty(200),
  description: optionalText(50_000),
  scheduleImpactDays: z.number().int().min(-3650).max(3650).default(0),
  lines: z.array(commitmentLineInput).min(1).max(500),
});
export const updateCommitmentChangeOrderInput = patchOf(createCommitmentChangeOrderInput.omit({ commitmentId: true }));
export const setCommitmentChangeOrderStatusInput = z.object({ id, status: commitmentChangeOrderStatus, reason: optionalText(2000) });
export const listCommitmentChangeOrdersInput = z.object({ projectId: id.optional(), commitmentId: id.optional() });

// ---------------------------------------------------------------------------
// Prime change order actions
// ---------------------------------------------------------------------------

export const sendChangeOrderInput = z.object({
  id,
  /** Extra recipients; the project's client contact is always included when it has an email. */
  to: z.array(email).max(10).default([]),
  message: optionalText(10_000),
});
export const setChangeOrderStatusInput = z.object({ id, status: z.enum(['draft', 'pending_internal', 'void']), reason: optionalText(2000) });

// ---------------------------------------------------------------------------
// Owner billing: schedule of values + pay applications (AIA G702/G703 style)
// ---------------------------------------------------------------------------

export const scheduleOfValuesInput = z.object({ projectId: id });
export const payApplicationLineInput = z.object({
  key: z.string().trim().min(1).max(80),
  description: nonEmpty(500),
  costCode: z.string().trim().max(40).optional().nullable(),
  budgetLineId: id.optional().nullable(),
  changeOrderId: id.optional().nullable(),
  scheduledValueCents: cents,
  /** Work completed this period (E). Either this or percentCompleteBps. */
  thisPeriodWorkCents: cents.optional().nullable(),
  /** Cumulative percent complete including stored materials (G / C). */
  percentCompleteBps: z.number().int().min(0).max(10_000).optional().nullable(),
  storedMaterialsCents: nonNegativeCents.default(0),
  taxable: z.boolean().default(false),
});
export type PayApplicationLineInput = z.infer<typeof payApplicationLineInput>;
export const createPayApplicationInput = z.object({
  projectId: id,
  contactId: id,
  companyId: id.optional().nullable(),
  periodStart: dateOnly.optional().nullable(),
  periodEnd: dateOnly,
  issueDate: dateOnly,
  dueDate: dateOnly,
  retainageBps: z.number().int().min(0).max(10_000).optional(),
  storedRetainageBps: z.number().int().min(0).max(10_000).optional(),
  taxRateBps: z.number().int().min(0).max(10_000).optional(),
  isFinal: z.boolean().default(false),
  memo: optionalText(10_000),
  allowCard: z.boolean().default(true),
  allowAch: z.boolean().default(true),
  lines: z.array(payApplicationLineInput).min(1).max(1000),
});
export type CreatePayApplicationInput = z.infer<typeof createPayApplicationInput>;

export const g703LineSchema = z.object({
  key: z.string(),
  description: z.string(),
  costCode: z.string().nullable().optional(),
  budgetLineId: z.string().nullable().optional(),
  changeOrderId: z.string().nullable().optional(),
  scheduledValueCents: cents,
  previousWorkCents: cents,
  previousStoredCents: cents,
  previousRetainageCents: cents,
  thisPeriodWorkCents: cents,
  storedMaterialsCents: cents,
  totalCompletedCents: cents,
  percentCompleteBps: z.number().int(),
  balanceToFinishCents: cents,
  retainageCents: cents,
  retainageThisPeriodCents: cents,
  grossThisPeriodCents: cents,
  taxable: z.boolean().optional(),
  isChangeOrder: z.boolean().optional(),
});
export type G703LineRow = z.infer<typeof g703LineSchema>;
export const g702SummarySchema = z.object({
  originalContractSumCents: cents,
  netChangeByChangeOrdersCents: cents,
  contractSumToDateCents: cents,
  totalCompletedAndStoredCents: cents,
  retainageOnCompletedWorkCents: cents,
  retainageOnStoredMaterialCents: cents,
  totalRetainageCents: cents,
  totalEarnedLessRetainageCents: cents,
  previousCertificatesCents: cents,
  currentPaymentDueCents: cents,
  balanceToFinishInclRetainageCents: cents,
  grossThisPeriodCents: cents,
  retainageThisPeriodCents: cents,
  taxableThisPeriodCents: cents,
  taxCents: cents,
  amountDueCents: cents,
});
/** Snapshot stored with a pay application invoice (immutable once sent). */
export const payApplicationSnapshotSchema = z.object({
  applicationNumber: z.number().int().min(1),
  periodStart: z.string().nullable(),
  periodEnd: z.string(),
  workRetainageBps: z.number().int(),
  storedRetainageBps: z.number().int(),
  isFinal: z.boolean(),
  lines: z.array(g703LineSchema),
  summary: g702SummarySchema,
});
export type PayApplicationSnapshot = z.infer<typeof payApplicationSnapshotSchema>;

export const createRetainageInvoiceInput = z.object({
  projectId: id,
  contactId: id,
  issueDate: dateOnly,
  dueDate: dateOnly,
  /** Defaults to all retainage currently held. */
  amountCents: nonNegativeCents.optional(),
  memo: optionalText(10_000),
});
export const voidInvoiceInput = z.object({ id, reason: optionalText(2000) });
/** Public invoice link (`/v1/financials/public/invoices/:token`, emailed when the invoice is sent). */
export const invoiceByTokenInput = z.object({ token: z.string().min(16).max(256) });

// ---------------------------------------------------------------------------
// Subcontractor pay applications received
// ---------------------------------------------------------------------------

export const SUB_PAY_APP_STATUSES = ['received', 'approved', 'rejected', 'paid', 'void'] as const;
export const subPayAppStatus = z.enum(SUB_PAY_APP_STATUSES);
export type SubPayAppStatus = z.infer<typeof subPayAppStatus>;

export const subPayAppLineInput = z.object({
  /** Commitment line id, or a commitment change order line id. */
  key: z.string().trim().min(1).max(80),
  thisPeriodWorkCents: cents.default(0),
  storedMaterialsCents: nonNegativeCents.default(0),
});
export const createSubPayAppInput = z.object({
  commitmentId: id,
  vendorInvoiceNumber: nonEmpty(60),
  periodEnd: dateOnly,
  receivedDate: dateOnly,
  dueDate: dateOnly.optional().nullable(),
  isFinal: z.boolean().default(false),
  notes: optionalText(5000),
  lines: z.array(subPayAppLineInput).min(1).max(1000),
});
export type CreateSubPayAppInput = z.infer<typeof createSubPayAppInput>;
export const setSubPayAppStatusInput = z.object({
  id,
  status: z.enum(['received', 'approved', 'rejected', 'void']),
  reason: optionalText(2000),
  /** Approve without the required conditional lien waiver (recorded in the audit log). */
  overrideWaivers: z.boolean().default(false),
});
export const markSubPayAppPaidInput = z.object({
  id,
  paidDate: dateOnly,
  method: paymentMethod,
  reference: z.string().trim().max(120).optional().nullable(),
  overrideWaivers: z.boolean().default(false),
});
export const listSubPayAppsInput = listInput.extend({ projectId: id.optional(), commitmentId: id.optional(), status: subPayAppStatus.optional() });
export const requestPayAppWaiverInput = z.object({
  id,
  stage: z.enum(['conditional', 'unconditional']),
  to: email.optional(),
});

// ---------------------------------------------------------------------------
// Lien waiver actions
// ---------------------------------------------------------------------------

export const requestLienWaiverInput = z.object({ id, to: email, message: optionalText(5000) });
export const lienWaiverByTokenInput = z.object({ token: z.string().min(16).max(256) });

// ---------------------------------------------------------------------------
// Accounting sync (QuickBooks Online, Sage Accounting)
// ---------------------------------------------------------------------------

export const ACCOUNTING_SYSTEMS = ['quickbooks_online', 'sage_accounting'] as const;
export const accountingSystem = z.enum(ACCOUNTING_SYSTEMS);
export type AccountingSystem = z.infer<typeof accountingSystem>;
export const ACCOUNTING_MAPPING_KINDS = ['cost_code', 'customer', 'vendor', 'income_account', 'expense_account', 'tax_code'] as const;
export const accountingMappingKind = z.enum(ACCOUNTING_MAPPING_KINDS);
export type AccountingMappingKind = z.infer<typeof accountingMappingKind>;
/** Remote lists the mapping screens search. `employee`: QuickBooks employees, for time-clock pushes. */
export const ACCOUNTING_ENTITY_KINDS = ['customer', 'vendor', 'item', 'account', 'employee'] as const;
export const accountingEntityKind = z.enum(ACCOUNTING_ENTITY_KINDS);
export type AccountingEntityKind = z.infer<typeof accountingEntityKind>;

export const connectAccountingInput = z.object({
  system: accountingSystem,
  environment: z.enum(['production', 'sandbox']).default('production'),
});
export const accountingConnectionInput = z.object({ connectionId: id });
export const setAccountingMappingInput = z.object({
  connectionId: id,
  kind: accountingMappingKind,
  /** Cost code, contact id, company id, or `default` for account / tax defaults. */
  localKey: z.string().trim().min(1).max(80),
  externalId: z.string().trim().min(1).max(120).nullable(),
  externalName: z.string().trim().max(300).optional().nullable(),
});
export const listExternalEntitiesInput = z.object({ connectionId: id, kind: accountingEntityKind, search: z.string().trim().max(100).optional() });
export const pushInvoiceInput = z.object({ connectionId: id, invoiceId: id });
export const pushBillInput = z.object({ connectionId: id, subPayAppId: id });
export const pullPaymentsInput = z.object({ connectionId: id, since: isoDate.optional() });
/** Push approved time entries as QuickBooks TimeActivity records: by id, or every unsynced one in a date range. */
export const pushTimeEntriesInput = z
  .object({
    connectionId: id,
    entryIds: z.array(id).min(1).max(200).optional(),
    from: dateOnly.optional(),
    to: dateOnly.optional(),
  })
  .refine((v) => v.entryIds || (v.from && v.to), { message: 'Pick entries or a date range' });
/** Map a workspace user to an existing QuickBooks employee or vendor (null clears it). */
export const setEmployeeMappingInput = z.object({
  connectionId: id,
  userId: id,
  externalId: z.string().trim().min(1).max(120).nullable(),
  externalName: z.string().trim().max(300).optional().nullable(),
  /** QuickBooks TimeActivity `NameOf`: employees by default, vendors for subcontracted labour. */
  nameOf: z.enum(['Employee', 'Vendor']).default('Employee'),
});

// ---------------------------------------------------------------------------
// Reports + lookups
// ---------------------------------------------------------------------------

export const jobCostReportInput = z.object({ projectId: id, method: eacMethod.default('auto') });
export const wipReportInput = z.object({ method: eacMethod.default('auto'), includeCompleted: z.boolean().default(false) });
export const arAgingInput = z.object({ asOf: dateOnly.optional(), projectId: id.optional() });
export const changeOrderLogInput = z.object({ projectId: id.optional(), includeCommitment: z.boolean().default(true) });
export const financialLookupInput = z.object({ search: z.string().trim().max(100).optional(), limit: z.number().int().min(1).max(100).default(25) });

// ===========================================================================
// Dedicated financial tables (migration 0004). Row schemas mirror the Drizzle tables
// in packages/db/src/schema/financials.ts. Money is integer cents, percentages basis points.
// ===========================================================================

// ---------------------------------------------------------------------------
// Cost code library (`cost_code`)
// ---------------------------------------------------------------------------

/** Two-digit CSI MasterFormat division (`03`); null for codes outside MasterFormat. */
const csiDivisionCode = z.string().regex(/^\d{2}$/, 'Two-digit CSI division');

export const createCostCodeInput = z.object({
  code: z.string().trim().min(1).max(40),
  title: nonEmpty(200),
  division: csiDivisionCode.optional().nullable(),
  parentId: id.optional().nullable(),
  category: costCategory.optional().nullable(),
  description: optionalText(2000),
  position: z.number().int().min(0).default(0),
});
export type CreateCostCodeInput = z.infer<typeof createCostCodeInput>;
export const updateCostCodeInput = patchOf(createCostCodeInput);
export type UpdateCostCodeInput = z.infer<typeof updateCostCodeInput>;
export const listCostCodeLibraryInput = listInput.extend({
  division: csiDivisionCode.optional(),
  parentId: id.optional().nullable(),
});
export const archiveCostCodeInput = z.object({ id, archived: z.boolean().default(true) });
export type ListCostCodeLibraryInput = z.infer<typeof listCostCodeLibraryInput>;
export const costCodeSchema = baseRow.extend({
  code: z.string(),
  title: z.string(),
  division: z.string().nullable(),
  parentId: id.nullable(),
  category: costCategory.nullable(),
  description: z.string().nullable(),
  position: z.number().int(),
});
export type CostCode = z.infer<typeof costCodeSchema>;

// ---------------------------------------------------------------------------
// Commitments (`commitment`, `commitment_line`)
// ---------------------------------------------------------------------------

export const commitmentRecordSchema = baseRow.extend({
  projectId: id,
  number: z.string(),
  kind: commitmentKind,
  status: commitmentStatus,
  title: z.string(),
  description: z.string().nullable(),
  companyId: id,
  contactId: id.nullable(),
  retainageBps: z.number().int(),
  /** Original contract value: sum of the commitment lines. */
  contractValueCents: cents,
  /** Sum of approved commitment change orders. */
  approvedChangesCents: cents,
  /** Sum of pending (submitted, undecided) commitment change orders. */
  pendingChangesCents: cents,
  /** Gross work billed on approved/paid sub pay apps (before retainage). */
  billedToDateCents: cents,
  retainageHeldCents: cents,
  paidToDateCents: cents,
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  sentAt: z.date().nullable(),
  executedAt: z.date().nullable(),
  closedAt: z.date().nullable(),
  voidedAt: z.date().nullable(),
  documentId: id.nullable(),
  bidId: id.nullable(),
  custom: customFieldValues,
});
export type CommitmentRecord = z.infer<typeof commitmentRecordSchema>;

export const commitmentLineRecordSchema = baseRow.extend({
  commitmentId: id,
  position: z.number().int(),
  costCodeId: id.nullable(),
  costCode: z.string().nullable(),
  category: costCategory,
  description: z.string(),
  quantity: z.number(),
  unit: z.string(),
  unitCostCents: cents,
  totalCents: cents,
  budgetLineId: id.nullable(),
});
export type CommitmentLineRecord = z.infer<typeof commitmentLineRecordSchema>;

// ---------------------------------------------------------------------------
// Change events (`change_event`, `change_event_line`)
// ---------------------------------------------------------------------------

export const changeEventRecordSchema = baseRow.extend({
  projectId: id,
  number: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  reason: changeReason,
  status: changeEventStatus,
  scope: changeEventScope,
  rfiId: id.nullable(),
  primeChangeOrderId: id.nullable(),
  estimatedCostCents: cents,
  estimatedPriceCents: cents,
  convertedAt: z.date().nullable(),
  custom: customFieldValues,
});
export type ChangeEventRecord = z.infer<typeof changeEventRecordSchema>;

export const changeEventLineRecordSchema = baseRow.extend({
  changeEventId: id,
  position: z.number().int(),
  costCodeId: id.nullable(),
  costCode: z.string().nullable(),
  category: costCategory,
  description: z.string(),
  quantity: z.number(),
  unit: z.string(),
  unitCostCents: cents,
  markupBps: z.number().int().nullable(),
  costTotalCents: cents,
  priceTotalCents: cents,
  budgetLineId: id.nullable(),
  commitmentId: id.nullable(),
  commitmentChangeOrderId: id.nullable(),
});
export type ChangeEventLineRecord = z.infer<typeof changeEventLineRecordSchema>;

// ---------------------------------------------------------------------------
// Commitment change orders (`commitment_change_order`, `commitment_change_order_line`)
// ---------------------------------------------------------------------------

export const commitmentChangeOrderRecordSchema = baseRow.extend({
  projectId: id,
  commitmentId: id,
  changeEventId: id.nullable(),
  number: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  status: commitmentChangeOrderStatus,
  scheduleImpactDays: z.number().int(),
  totalCents: cents,
  decidedAt: z.date().nullable(),
  decidedBy: id.nullable(),
  rejectionReason: z.string().nullable(),
  custom: customFieldValues,
});
export type CommitmentChangeOrderRecord = z.infer<typeof commitmentChangeOrderRecordSchema>;

export const commitmentChangeOrderLineRecordSchema = baseRow.extend({
  commitmentChangeOrderId: id,
  position: z.number().int(),
  costCodeId: id.nullable(),
  costCode: z.string().nullable(),
  category: costCategory,
  description: z.string(),
  quantity: z.number(),
  unit: z.string(),
  unitCostCents: cents,
  totalCents: cents,
  budgetLineId: id.nullable(),
  changeEventLineId: id.nullable(),
});
export type CommitmentChangeOrderLineRecord = z.infer<typeof commitmentChangeOrderLineRecordSchema>;

// ---------------------------------------------------------------------------
// Subcontractor pay applications (`sub_pay_app`, `sub_pay_app_line`)
// ---------------------------------------------------------------------------

/** budget_line id -> cost cents applied when the pay app was approved (reversed exactly on void). */
export const appliedActualsSchema = z.record(z.string(), z.number().int());
export type AppliedActuals = z.infer<typeof appliedActualsSchema>;
export type G702Summary = z.infer<typeof g702SummarySchema>;

export const subPayAppRecordSchema = baseRow.extend({
  projectId: id,
  commitmentId: id,
  companyId: id,
  contactId: id.nullable(),
  applicationNumber: z.number().int(),
  vendorInvoiceNumber: z.string(),
  status: subPayAppStatus,
  periodStart: z.string().nullable(),
  periodEnd: z.string(),
  receivedDate: z.string(),
  dueDate: z.string().nullable(),
  isFinal: z.boolean(),
  notes: z.string().nullable(),
  retainageBps: z.number().int(),
  scheduledValueCents: cents,
  totalCompletedCents: cents,
  grossThisPeriodCents: cents,
  retainageThisPeriodCents: cents,
  totalRetainageCents: cents,
  /** Current payment due: gross this period less retainage this period. */
  amountDueCents: cents,
  summary: g702SummarySchema.nullable(),
  appliedActuals: appliedActualsSchema,
  approvedAt: z.date().nullable(),
  approvedBy: id.nullable(),
  waiverOverride: z.boolean(),
  rejectionReason: z.string().nullable(),
  paidAt: z.date().nullable(),
  paidDate: z.string().nullable(),
  paymentMethod: paymentMethod.nullable(),
  paymentReference: z.string().nullable(),
  custom: customFieldValues,
});
export type SubPayAppRecord = z.infer<typeof subPayAppRecordSchema>;

/** One schedule-of-values row of a sub pay app (AIA G703 columns). */
export const subPayAppLineRecordSchema = baseRow.extend({
  subPayAppId: id,
  position: z.number().int(),
  commitmentLineId: id.nullable(),
  commitmentChangeOrderLineId: id.nullable(),
  description: z.string(),
  costCodeId: id.nullable(),
  costCode: z.string().nullable(),
  budgetLineId: id.nullable(),
  scheduledValueCents: cents,
  previousWorkCents: cents,
  previousStoredCents: cents,
  previousRetainageCents: cents,
  thisPeriodWorkCents: cents,
  storedMaterialsCents: cents,
  totalCompletedCents: cents,
  percentCompleteBps: z.number().int(),
  balanceToFinishCents: cents,
  retainageCents: cents,
  retainageThisPeriodCents: cents,
  grossThisPeriodCents: cents,
});
export type SubPayAppLineRecord = z.infer<typeof subPayAppLineRecordSchema>;

// ---------------------------------------------------------------------------
// Accounting sync (`accounting_mapping`, `accounting_sync_log`)
// ---------------------------------------------------------------------------

/**
 * What an `accounting_mapping` row maps. The first six are user-configured lookups
 * (`ACCOUNTING_MAPPING_KINDS`); the rest record documents BuildBee pushed or pulled.
 */
export const ACCOUNTING_MAPPED_ENTITIES = [
  'cost_code',
  'customer',
  'vendor',
  'income_account',
  'expense_account',
  'tax_code',
  'invoice',
  'sub_pay_app',
  'payment',
  /** Workspace user -> QuickBooks Employee (or Vendor, for contractors on the time clock). */
  'employee',
  /** Approved time entry pushed as a QuickBooks TimeActivity. */
  'time_entry',
] as const satisfies readonly (AccountingMappingKind | 'invoice' | 'sub_pay_app' | 'payment' | 'employee' | 'time_entry')[];
export const accountingMappedEntity = z.enum(ACCOUNTING_MAPPED_ENTITIES);
export type AccountingMappedEntity = z.infer<typeof accountingMappedEntity>;

export const ACCOUNTING_SYNC_DIRECTIONS = ['push', 'pull'] as const;
export const accountingSyncDirection = z.enum(ACCOUNTING_SYNC_DIRECTIONS);
export type AccountingSyncDirection = z.infer<typeof accountingSyncDirection>;

export const ACCOUNTING_SYNC_STATUSES = ['succeeded', 'failed', 'skipped'] as const;
export const accountingSyncStatus = z.enum(ACCOUNTING_SYNC_STATUSES);
export type AccountingSyncStatus = z.infer<typeof accountingSyncStatus>;

export const accountingMappingSchema = baseRow.extend({
  connectionId: id,
  system: accountingSystem,
  entityType: accountingMappedEntity,
  /** Local row id when the local side is a row (contact, company, invoice, ...). */
  localId: id.nullable(),
  /** Stable lookup key: the local id as text, a cost code, or `default`. Unique per connection + entity type. */
  localKey: z.string(),
  remoteId: z.string(),
  remoteName: z.string().nullable(),
  /** Remote concurrency token (QuickBooks SyncToken, Sage etag). */
  remoteVersion: z.string().nullable(),
  lastSyncedAt: z.date().nullable(),
  metadata: z.record(z.string(), z.unknown()),
});
export type AccountingMapping = z.infer<typeof accountingMappingSchema>;

export const accountingSyncLogSchema = baseRow.extend({
  connectionId: id,
  system: accountingSystem,
  direction: accountingSyncDirection,
  /** e.g. `invoice.push`, `bill.push`, `payments.pull`, `vendor.create`. */
  operation: z.string(),
  entityType: accountingMappedEntity.nullable(),
  localId: id.nullable(),
  remoteId: z.string().nullable(),
  status: accountingSyncStatus,
  error: z.string().nullable(),
  details: z.record(z.string(), z.unknown()),
  startedAt: z.date(),
  durationMs: z.number().int().nullable(),
});
export type AccountingSyncLog = z.infer<typeof accountingSyncLogSchema>;
export const listAccountingSyncLogInput = listInput.extend({
  connectionId: id.optional(),
  status: accountingSyncStatus.optional(),
  entityType: accountingMappedEntity.optional(),
  localId: id.optional(),
});
